import { createAsyncThunk } from '@reduxjs/toolkit';
import { FeedMessagesAmount, patientService } from 'services/patientService';
import { FeedFilter } from 'store/dashboard/slice';
import { GetPatientCasesParams, TreatmentPlanTask, User, UserRole } from 'types';

const basePath = 'dashbord/';

enum DashboardPaths {
  GET_PATIENT_CASES_NEW = 'getPatientCasesDashboardNew',
  GET_PATIENT_CASES_LETTER = 'getPatientCasesDashboardLetter',
  GET_PATIENT_CASES_IN_TREATMENT = 'getPatientCasesDashboardInTreatment',
  GET_PATIENT_CASES_FINISHED = 'getPatientCasesDashboardFinished',
  GET_PATIENT_CASES_OOT = 'getPatientCasesDashboardOutOfTreatment',
  GET_FEED_AMOUNT = 'getFeedMessagesAmount',
  GET_FEED = 'getFeed',
  DISMISS_PATIENT_CASE = 'dismissPatientCase',
}

const getActionName = (path: DashboardPaths) => basePath + path;

type PartialStoreType = {
  dashboard: {
    dashboardNewCasesFilter: GetPatientCasesParams;
    dashboardLetterCasesFilter: GetPatientCasesParams;
    dashboardInTreatmentCasesFilter: GetPatientCasesParams;
    dashboardFinishedCasesFilter: GetPatientCasesParams;
    dashboardOutOfTreatmentCasesFilter: GetPatientCasesParams;
  };
  auth: {
    currentUser: User;
  };
};

export const feedTasks: Record<keyof FeedMessagesAmount, TreatmentPlanTask[]> = {
  newPatients: [TreatmentPlanTask.DIAGNOSIS_TO_DO, TreatmentPlanTask.DIAGNOSIS_SUBMITTED],
  letterReview: [TreatmentPlanTask.LETTER_TO_DO, TreatmentPlanTask.LETTER_SUBMITTED, TreatmentPlanTask.LETTER_SENT],
  inTreatment: [TreatmentPlanTask.IN_TREATMENT],
  finished: [TreatmentPlanTask.TREATMENT_PRE_FINISH, TreatmentPlanTask.TREATMENT_POST_FINISH],
  outOfTreatment: [
    TreatmentPlanTask.COMPLETED,
    TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
    TreatmentPlanTask.LETTER_SENT,
    TreatmentPlanTask.LETTER_SUBMITTED,
    TreatmentPlanTask.DIAGNOSIS_TO_DO,
    TreatmentPlanTask.LETTER_TO_DO,
    TreatmentPlanTask.TO_SUBMIT,
    TreatmentPlanTask.TREATMENT_POST_FINISH,
    TreatmentPlanTask.TREATMENT_PRE_FINISH,
    TreatmentPlanTask.IN_TREATMENT,
  ],
};

const getPatientCasesDashboardNew = createAsyncThunk(
  getActionName(DashboardPaths.GET_PATIENT_CASES_NEW),
  async (_, thunkAPI) => {
    const {
      dashboard: { dashboardNewCasesFilter },
      auth: {
        currentUser: { id, role },
      },
    } = thunkAPI.getState() as PartialStoreType;

    const roleDependantFilter: GetPatientCasesParams = {};

    if (role === UserRole.DOCTOR) {
      roleDependantFilter.doctorIds = [id];
    }

    return await patientService.getPatientCases({
      ...dashboardNewCasesFilter,
      tasks: [TreatmentPlanTask.DIAGNOSIS_TO_DO, TreatmentPlanTask.DIAGNOSIS_SUBMITTED],
      isTaskBlocked: false,
      ...roleDependantFilter,
    });
  },
);

const getPatientCasesDashboardLetter = createAsyncThunk(
  getActionName(DashboardPaths.GET_PATIENT_CASES_LETTER),
  async (_, thunkAPI) => {
    const {
      dashboard: { dashboardLetterCasesFilter },
      auth: {
        currentUser: { id, role },
      },
    } = thunkAPI.getState() as PartialStoreType;

    const roleDependantFilter: GetPatientCasesParams = {};

    if (role === UserRole.DOCTOR) {
      roleDependantFilter.doctorIds = [id];
    }

    return await patientService.getPatientCases({
      ...dashboardLetterCasesFilter,
      tasks: [TreatmentPlanTask.LETTER_TO_DO, TreatmentPlanTask.LETTER_SUBMITTED, TreatmentPlanTask.LETTER_SENT],
      isTaskBlocked: false,
      ...roleDependantFilter,
    });
  },
);

const getPatientCasesDashboardInTreatment = createAsyncThunk(
  getActionName(DashboardPaths.GET_PATIENT_CASES_IN_TREATMENT),
  async (_, thunkAPI) => {
    const {
      dashboard: { dashboardInTreatmentCasesFilter },
      auth: {
        currentUser: { id, role },
      },
    } = thunkAPI.getState() as PartialStoreType;
    const roleDependantFilter: GetPatientCasesParams = {};

    if (role === UserRole.DOCTOR) {
      roleDependantFilter.doctorIds = [id];
    }

    return await patientService.getPatientCases({
      ...dashboardInTreatmentCasesFilter,
      tasks: [TreatmentPlanTask.IN_TREATMENT],
      isOnlyWithMessagesForCurrentUser: true,
      isExcludeDismissed: true,
      isTaskBlocked: false,
      ...roleDependantFilter,
    });
  },
);

const getPatientCasesDashboardFinished = createAsyncThunk(
  getActionName(DashboardPaths.GET_PATIENT_CASES_FINISHED),
  async (_, thunkAPI) => {
    const {
      dashboard: { dashboardFinishedCasesFilter },
      auth: {
        currentUser: { id, role },
      },
    } = thunkAPI.getState() as PartialStoreType;

    const roleDependantFilter: GetPatientCasesParams = {};

    if (role === UserRole.DOCTOR) {
      roleDependantFilter.doctorIds = [id];
    }

    return await patientService.getPatientCases({
      ...dashboardFinishedCasesFilter,
      tasks: [TreatmentPlanTask.TREATMENT_PRE_FINISH, TreatmentPlanTask.TREATMENT_POST_FINISH],
      isOnlyWithMessagesForCurrentUser: true,
      isExcludeDismissed: true,
      isTaskBlocked: false,
      ...roleDependantFilter,
    });
  },
);

const getPatientCasesDashboardOutOfTreatment = createAsyncThunk(
  getActionName(DashboardPaths.GET_PATIENT_CASES_OOT),
  async (_, thunkAPI) => {
    const {
      dashboard: { dashboardOutOfTreatmentCasesFilter },
      auth: {
        currentUser: { id, role },
      },
    } = thunkAPI.getState() as PartialStoreType;

    const roleDependantFilter: GetPatientCasesParams = {};

    if (role === UserRole.DOCTOR) {
      roleDependantFilter.doctorIds = [id];
    }

    return await patientService.getPatientCases({
      ...dashboardOutOfTreatmentCasesFilter,
      tasks: [
        TreatmentPlanTask.TREATMENT_PRE_FINISH,
        TreatmentPlanTask.TREATMENT_POST_FINISH,
        TreatmentPlanTask.IN_TREATMENT,
        TreatmentPlanTask.COMPLETED,
        TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
        TreatmentPlanTask.LETTER_SUBMITTED,
        TreatmentPlanTask.LETTER_SENT,
        TreatmentPlanTask.DIAGNOSIS_TO_DO,
        TreatmentPlanTask.LETTER_TO_DO,
        TreatmentPlanTask.TO_SUBMIT,
      ],
      isOnlyWithMessagesForCurrentUser: true,
      isExcludeDismissed: true,
      isTaskBlocked: true,
      ...roleDependantFilter,
    });
  },
);

const getFeedMessagesAmount = createAsyncThunk(getActionName(DashboardPaths.GET_FEED_AMOUNT), async () => {
  return await patientService.getFeedMessagesAmount();
});

const getFeed = createAsyncThunk(
  getActionName(DashboardPaths.GET_FEED),
  async (key: keyof FeedMessagesAmount, thunkAPI) => {
    const tasks = feedTasks[key];
    const {
      dashboard: { feedFilter },
    } = thunkAPI.getState() as { dashboard: { feedFilter: FeedFilter } };
    return await patientService.getFeedMessages({
      tasks,
      page: feedFilter.page,
      size: feedFilter.size,
      isTaskBlocked: key === 'outOfTreatment',
    });
  },
);

const dismissPatientCase = createAsyncThunk(
  getActionName(DashboardPaths.DISMISS_PATIENT_CASE),
  async (patientCaseId: number) => {
    return await patientService.dismissPatientCase(patientCaseId);
  },
);

export const asyncDashboardActions = {
  getPatientCasesDashboardNew,
  getPatientCasesDashboardLetter,
  getPatientCasesDashboardInTreatment,
  getPatientCasesDashboardFinished,
  getFeedMessagesAmount,
  getFeed,
  dismissPatientCase,
  getPatientCasesDashboardOutOfTreatment,
};
